import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Alert, Spinner, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import AdminToolsLayout from './AdminToolsLayout';

/**
 * Delete Review Component
 * Review and manage hidden articles (soft deleted)
 */
const DeleteReview = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionTarget, setActionTarget] = useState(null);
  const [actionType, setActionType] = useState(null); // 'restore' or 'delete'
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchHiddenArticles();
  }, []);

  const fetchHiddenArticles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/tools/articles/hidden', {
        credentials: 'include'
      });
      const data = await response.json();
      setArticles(data.articles || []);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch hidden articles');
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!actionTarget) return;
    setProcessing(true);
    setError('');

    try {
      const response = await fetch(`/api/admin/tools/articles/${actionTarget.id}/unhide`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setActionTarget(null);
        setActionType(null);
        fetchHiddenArticles();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to restore article');
      }
    } catch (err) {
      setError('Failed to restore article');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!actionTarget) return;
    setProcessing(true);
    setError('');

    try {
      const response = await fetch(`/api/admin/tools/articles/${actionTarget.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setActionTarget(null);
        setActionType(null);
        fetchHiddenArticles();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete article');
      }
    } catch (err) {
      setError('Failed to delete article');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const truncate = (str, len = 40) => {
    if (!str) return '-';
    return str.length > len ? str.substring(0, len) + '...' : str;
  };

  if (loading) {
    return (
      <AdminToolsLayout>
        <div className="text-center py-4"><Spinner animation="border" /></div>
      </AdminToolsLayout>
    );
  }

  return (
    <AdminToolsLayout>
      <Card>
        <Card.Header>
          <h5 className="mb-0">Delete Review</h5>
          <small className="text-muted">
            Articles hidden by users awaiting your review
          </small>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {articles.length === 0 ? (
            <Alert variant="info">
              No articles pending review. Users can hide articles they want deleted.
            </Alert>
          ) : (
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Source</th>
                  <th>Hidden</th>
                  <th>Reason</th>
                  <th>Cost</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => (
                  <tr key={article.id}>
                    <td>
                      <Link
                        to={`/tools/article-to-media/${article.slug}`}
                        target="_blank"
                        style={{ textDecoration: 'none' }}
                      >
                        {truncate(article.article_title, 30)}
                      </Link>
                    </td>
                    <td>{article.source_domain || '-'}</td>
                    <td style={{ fontSize: '0.875rem' }}>{formatDate(article.hidden_at)}</td>
                    <td style={{ fontSize: '0.875rem' }}>{truncate(article.hidden_reason, 30)}</td>
                    <td>${parseFloat(article.total_cost || 0).toFixed(4)}</td>
                    <td>
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="me-1"
                        onClick={() => {
                          setActionTarget(article);
                          setActionType('restore');
                        }}
                      >
                        Restore
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setActionTarget(article);
                          setActionType('delete');
                        }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Action Confirmation Modal */}
      <Modal show={!!actionTarget} onHide={() => { setActionTarget(null); setActionType(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === 'restore' ? 'Restore Article' : 'Permanently Delete Article'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionType === 'restore' ? (
            <p>This will restore the article and make it visible again.</p>
          ) : (
            <>
              <p>This will <strong>permanently delete</strong> the article and all associated files.</p>
              <Alert variant="warning">
                This action cannot be undone. Cost records will be preserved.
              </Alert>
            </>
          )}
          {actionTarget && (
            <p><strong>{actionTarget.article_title}</strong></p>
          )}
          {actionTarget?.hidden_reason && (
            <p className="text-muted">
              <small>User's reason: {actionTarget.hidden_reason}</small>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setActionTarget(null); setActionType(null); }}>
            Cancel
          </Button>
          <Button
            variant={actionType === 'restore' ? 'success' : 'danger'}
            onClick={actionType === 'restore' ? handleRestore : handleDelete}
            disabled={processing}
          >
            {processing ? 'Processing...' : (actionType === 'restore' ? 'Restore' : 'Delete Permanently')}
          </Button>
        </Modal.Footer>
      </Modal>
    </AdminToolsLayout>
  );
};

export default DeleteReview;
